/////////////////////////////////////////////////////////////////////////////////////////////////////////
//  컴포넌트명 : LoginPromo (로그인 화면 "이번 달 시책" 광고 카드)
//  설명 : HomePageU1의 "이번 달 시책" 카드(시책 배너 자동 슬라이드)를 로그인 화면에 광고성으로 보여주는 카드.
//        - 데이터는 utils/homeBanners.ts(HomePageU1과 공용, 현재 목업). 로그인 전 화면이라 서버 호출 없음.
//        - 4.5초마다 자동 슬라이드, 마우스를 올리거나 포커스가 있으면 멈춤, 점(dots)으로 직접 이동.
//          모션 줄이기 설정/안 보이는 배치(같은 화면에서 CSS로 숨겨진 복제본)/백그라운드 탭에서는 넘기지 않음.
//        - variant : 배치별 로그인 화면 색상 - 'brand'(브랜드 패널, 유리) / 'sheet'(좁은 화면, 인디고-보라 카드).
//          시책 데이터의 카드별 색(grad)은 쓰지 않고 로그인 화면 팔레트로 통일함.
//  개발이력 : 2026.09.21  최초개발 / 카드 색상을 로그인 화면 팔레트(배치별 variant)로 재구성
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useEffect, useRef, useState } from 'react';
import '../styles/loginPromo.css';
import { HOME_BANNERS } from '../utils/homeBanners';
import { useCommonTranslation } from '../translations';

interface LoginPromoProps {
  variant: 'brand' | 'sheet';
  className?: string;
}

const AUTO_MS = 4500;

const LoginPromo: React.FC<LoginPromoProps> = ({ variant, className }) => {
  const { ddKey } = useCommonTranslation();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (paused || HOME_BANNERS.length < 2) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const t = window.setInterval(() => {
      // 화면에 안 보이는 동안(CSS로 숨겨진 배치, 백그라운드 탭)에는 넘기지 않음
      if (document.visibilityState !== 'visible' || !rootRef.current || rootRef.current.getClientRects().length === 0) return;
      setIdx(i => (i + 1) % HOME_BANNERS.length);
    }, AUTO_MS);
    return () => window.clearInterval(t);
  }, [paused]);

  const title = ddKey('lblThisMonthIncentive') || '이번 달 시책';

  return (
    <section
      ref={rootRef}
      className={`lpromo lpromo--${variant}${className ? ` ${className}` : ''}`}
      aria-label={title}
      aria-roledescription="carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="lpromo-head">
        <h3 className="lpromo-title"><i className="fas fa-gift" aria-hidden="true" />{title}</h3>
        <span className="lpromo-ad">{ddKey('lblPromoNotice') || '알림'}</span>
        <div className="lpromo-dots">
          {HOME_BANNERS.map((_, i) => (
            <button key={i} type="button" className={i === idx ? 'on' : ''} onClick={() => setIdx(i)} aria-label={`${i + 1} / ${HOME_BANNERS.length}`} aria-current={i === idx} />
          ))}
        </div>
      </div>
      <div className="lpromo-slides">
        <div className="lpromo-track" style={{ transform: `translateX(-${idx * 100}%)` }}>
          {HOME_BANNERS.map((b, i) => (
            <div key={b.title} className="lpromo-slide" aria-hidden={i !== idx}>
              <div className="lpromo-text"><span>{b.carrier}</span><h4>{b.title}</h4><p>{b.sub}</p></div>
              <div className="lpromo-rate"><b>{b.rate}</b><em>{b.dday}</em></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LoginPromo;
